import { useState } from "react";
import { useTranslation } from "react-i18next";
import { extractErrorMessage } from "@/shared/utils";
import {
  useCreateItemRequestMutation,
  useApproveItemRequestMutation,
  useRejectItemRequestMutation,
  useCancelItemRequestMutation,
  useFulfillItemRequestMutation,
  useMarkUnavailableItemRequestMutation,
} from "../api";
import type { CreateItemRequestDto, RejectRequestDto, FulfillRequestDto, MarkUnavailableDto } from "../types";
import type { PromptState } from "./useWarehouseModals";

export interface UseItemRequestsMutationsReturn {
  isCreating: boolean;
  approvingId: number | null;
  rejectingId: number | null;
  cancellingId: number | null;
  fulfillingId: number | null;
  markingUnavailableId: number | null;
  handleCreate: (dto: CreateItemRequestDto) => Promise<void>;
  handleApprove: (id: number) => Promise<void>;
  handleReject: (id: number, dto: RejectRequestDto) => Promise<void>;
  handleCancel: (id: number) => Promise<void>;
  handleFulfill: (id: number, dto: FulfillRequestDto) => Promise<void>;
  handleMarkUnavailable: (id: number, dto: MarkUnavailableDto) => Promise<void>;
}

export function useItemRequestsMutations(
  setPrompt: (state: PromptState | null) => void,
): UseItemRequestsMutationsReturn {
  const { t } = useTranslation("WarehousePage");

  const [createItemRequest] = useCreateItemRequestMutation();
  const [approveItemRequest] = useApproveItemRequestMutation();
  const [rejectItemRequest] = useRejectItemRequestMutation();
  const [cancelItemRequest] = useCancelItemRequestMutation();
  const [fulfillItemRequest] = useFulfillItemRequestMutation();
  const [markUnavailableItemRequest] = useMarkUnavailableItemRequestMutation();

  const [isCreating, setIsCreating] = useState(false);
  const [approvingId, setApprovingId] = useState<number | null>(null);
  const [rejectingId, setRejectingId] = useState<number | null>(null);
  const [cancellingId, setCancellingId] = useState<number | null>(null);
  const [fulfillingId, setFulfillingId] = useState<number | null>(null);
  const [markingUnavailableId, setMarkingUnavailableId] = useState<number | null>(null);

  async function handleCreate(dto: CreateItemRequestDto) {
    if (isCreating) return;
    try {
      setIsCreating(true);
      await createItemRequest(dto).unwrap();
      setPrompt({
        title: t("requests.messages.createSuccessTitle"),
        text: t("requests.messages.createSuccessText"),
        variant: "success",
      });
    } catch (err: unknown) {
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(err, "requests.messages.createErrorText", t),
        variant: "error",
      });
    } finally {
      setIsCreating(false);
    }
  }

  async function handleApprove(id: number) {
    if (approvingId !== null) return;
    try {
      setApprovingId(id);
      await approveItemRequest(id).unwrap();
      setPrompt({
        title: t("requests.messages.approveSuccessTitle"),
        text: t("requests.messages.approveSuccessText"),
        variant: "success",
      });
    } catch (err: unknown) {
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(err, "requests.messages.approveErrorText", t),
        variant: "error",
      });
    } finally {
      setApprovingId(null);
    }
  }

  async function handleReject(id: number, dto: RejectRequestDto) {
    if (rejectingId !== null) return;
    try {
      setRejectingId(id);
      await rejectItemRequest({ id, body: dto }).unwrap();
      setPrompt({
        title: t("requests.messages.rejectSuccessTitle"),
        text: t("requests.messages.rejectSuccessText"),
        variant: "success",
      });
    } catch (err: unknown) {
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(err, "requests.messages.rejectErrorText", t),
        variant: "error",
      });
    } finally {
      setRejectingId(null);
    }
  }

  async function handleCancel(id: number) {
    if (cancellingId !== null) return;
    try {
      setCancellingId(id);
      await cancelItemRequest(id).unwrap();
      setPrompt({
        title: t("requests.messages.cancelSuccessTitle"),
        text: t("requests.messages.cancelSuccessText"),
        variant: "success",
      });
    } catch (err: unknown) {
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(err, "requests.messages.cancelErrorText", t),
        variant: "error",
      });
    } finally {
      setCancellingId(null);
    }
  }

  async function handleFulfill(id: number, dto: FulfillRequestDto) {
    if (fulfillingId !== null) return;
    try {
      setFulfillingId(id);
      await fulfillItemRequest({ id, body: dto }).unwrap();
      setPrompt({
        title: t("requests.messages.fulfillSuccessTitle"),
        text: t("requests.messages.fulfillSuccessText"),
        variant: "success",
      });
    } catch (err: unknown) {
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(err, "requests.messages.fulfillErrorText", t),
        variant: "error",
      });
    } finally {
      setFulfillingId(null);
    }
  }

  async function handleMarkUnavailable(id: number, dto: MarkUnavailableDto) {
    if (markingUnavailableId !== null) return;
    try {
      setMarkingUnavailableId(id);
      await markUnavailableItemRequest({ id, body: dto }).unwrap();
      setPrompt({
        title: t("requests.messages.markUnavailableSuccessTitle"),
        text: t("requests.messages.markUnavailableSuccessText"),
        variant: "success",
      });
    } catch (err: unknown) {
      setPrompt({
        title: t("messages.errorTitle"),
        text: extractErrorMessage(err, "requests.messages.markUnavailableErrorText", t),
        variant: "error",
      });
    } finally {
      setMarkingUnavailableId(null);
    }
  }

  return {
    isCreating,
    approvingId,
    rejectingId,
    cancellingId,
    fulfillingId,
    markingUnavailableId,
    handleCreate,
    handleApprove,
    handleReject,
    handleCancel,
    handleFulfill,
    handleMarkUnavailable,
  };
}
